/* eslint-disable */
// @ts-nocheck

import RadialBarChart from '@components/RadialBarChart';
import { ResultType } from '@data/types';
import useQuestionnaireStore from '@store/questionnaire';
import useStore from '@store/store';
import React, { useEffect, useMemo, useState } from 'react';
import { submitSurvey } from 'utilities/api';
import { hash } from 'utilities/helper';
import { getPillarScores, getRegion } from 'utilities/scores';
import ModalText from './ModalText';

import Button from './Button';
import Prose from './Prose';
import ResultRow from './ResultsRow';

interface Props extends ResultType {}

const Results: React.FC<Props> = ({
  displayResults = false,
  pillarIndex,
  htmlContent,
}) => {
  const [displayModal, setDisplayModal] = useState<boolean>(false);
  const [buttonText, setButtonText] = useState<string>('');
  const [isFadingIn, setIsFadingIn] = useState<boolean>(false);
  const [isLastQuestion, setIsLastQuestion] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const {
    pillarsStore,
    setPillarIndexStore,
    setDisplayResultsStore,
    setQuestionIndexStore,
    pillarsLengthStore,
    displayEndResultsStore,
    setDisplayEndResultsStore,
    firstName,
    lastName,
    email,
    company,
    privacyPolicy,
  } = useQuestionnaireStore();
  const { setPage } = useStore();

  useEffect(() => {
    if (displayResults) {
      setTimeout(() => setIsFadingIn(true), 50);
    } else {
      setIsFadingIn(false);
    }
  }, [displayResults]);

  // Logic to determine button functionality
  useEffect(() => {
    setIsLastQuestion(pillarIndex + 1 === pillarsLengthStore);
  }, []);

  useEffect(() => {
    if (isLastQuestion) {
      setButtonText('Get results');
    } else {
      setButtonText('Continue to the next pillar');
    }
  }, [isLastQuestion]);

  const buttonOnClick = async () => {
    // Scroll to top of page
    window.scrollTo(0, 0);

    if (isLastQuestion) {
      // Send the results to the API
      const response = await submitSurvey({
        firstName: firstName,
        lastName: lastName,
        companyName: company,
        emailAddress: email,
        privacyPolicy: privacyPolicy,
        pillarsStore,
      });

      if (!response.success) {
        console.error(response.error);
        setErrorMessage(
          response.error || 'An error occurred. Please try again.'
        );
        setDisplayModal(!displayModal);

        return;
      }

      // If a success response, go to the final results page
      setDisplayEndResultsStore(!displayEndResultsStore);
      setPage('results');

      return;
    }

    // Increase the pillar index
    setPillarIndexStore(pillarIndex + 1);

    // Reset the question index
    setQuestionIndexStore(0);

    // Hide the results page
    setDisplayResultsStore(false);
  };

  const [region, score, globalAverage, regionalAverage, marketLeadersAverage] =
    useMemo(
      function () {
        const region = getRegion(pillarsStore);

        return [region, ...getPillarScores(pillarIndex, pillarsStore, region)];
      },
      [pillarIndex, pillarsStore]
    );

  const resultsRows = [
    {
      iconColour: 'bg-purple-light',
      label: 'Your overall score',
      score: score,
    },
    {
      iconColour: 'bg-blue-light',
      label: 'Global (average)',
      score: globalAverage,
    },
    {
      iconColour: 'bg-green',
      label: `Regional (average): ${region}`,
      score: regionalAverage,
    },
    {
      iconColour: 'bg-yellow',
      label: 'Market leaders (average)',
      score: marketLeadersAverage,
    },
  ];

  return (
    <li className="w-full h-full flex flex-row md:absolute md:inset-0 md:overflow-visible">
      <div className="flex flex-col md:flex-row w-full h-full">
        <div className="w-full md:w-1/2 relative hidden md:block">&nbsp;</div>
        <div className="w-full md:w-1/2 bg-grey flex flex-col relative z-20">
          <span
            className={`absolute top-0 right-[-100vw] bottom-0 left-0 bg-blue transition-opacity duration-200 ease-in ${isFadingIn ? 'opacity-100' : 'opacity-0'}`}
          >
            &nbsp;
          </span>
          <div className="px-4 py-8 md:py-16 md:px-12 flex flex-col h-full overflow-y-scroll">
            <div className="relative flex flex-col h-full text-white">
              <div>
                <h2 className="font-lightNormal text-xs bg-blue-light text-blue rounded-tl-lg rounded-br-lg lg:max-w-md mb-4 inline-block uppercase px-4 py-1">
                  Pillar {pillarIndex}
                </h2>
              </div>
              <div className="flex flex-col lg:flex-row items-center lg:gap-6 mt-8">
                <div className="w-full lg:w-1/2 order-2 lg:order-1">
                  <div className="hidden lg:block">
                    <h3 className="text-1xl font-medium mb-4 leading-tight mb-0">
                      Your pillar score
                    </h3>
                    <em className="text-[76px] font-lightNormal not-italic tracking-normal inline-block text-purple">
                      {score}
                      <small className="text-[35px]">/20</small>
                    </em>
                  </div>

                  <table className="w-full text-xs font-lightNormal">
                    <thead className="hidden">
                      <tr>
                        <th>icon</th>
                        <th>description</th>
                        <th>rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultsRows &&
                        resultsRows.map((row) => (
                          <ResultRow
                            iconColour={row.iconColour}
                            label={row.label}
                            score={row.score}
                            key={hash(row)}
                          />
                        ))}
                    </tbody>
                  </table>
                </div>
                <div className="w-full lg:w-1/2 order-1 lg:order-2">
                  <h3 className="text-1xl font-medium leading-tight w-1/2 md:w-full lg:hidden">
                    Your pillar score
                  </h3>
                  <div className="flex flex-row gap-4 md:mt-4">
                    <div className="block lg:hidden w-2/5">
                      <em className="text-[56px] lg:text-[76px] font-lightNormal not-italic tracking-normal inline-block text-purple">
                        {score}
                        <small className="text-[35px]">/20</small>
                      </em>
                    </div>
                    <div className="w-3/5 lg:w-full">
                      <RadialBarChart
                        question="Question"
                        region={region}
                        scores={[
                          score,
                          globalAverage,
                          regionalAverage,
                          marketLeadersAverage,
                        ]}
                        max={20}
                        className="-mt-16 md:mt-0"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-8 md:mt-12">
                <Prose htmlContent={htmlContent} style="style-4" />
              </div>
              <div className="mt-auto">
                <Button
                  title={buttonText}
                  type="button"
                  className="!bg-red mt-4 mb-2 !text-white"
                  onClick={buttonOnClick}
                />
                <small className="block mt-6 text-white/[0.7]">
                  View and download your in-depth results by completing the full
                  survey
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
      {displayModal && (
        <ModalText
          setDisplayModal={setDisplayModal}
          displayModal={displayModal}
          message={errorMessage}
        />
      )}
    </li>
  );
};

export default Results;
